import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openMediaViewModal } from '../../actions/modal';
import { updateAssets } from '../../actions/inventory';
import { getServiceRequestById } from '../../actions/service_request';
import { sendToastMessage } from '../../actions/toast';
import { postImageUrl } from '../../actions/users';
import Loader from '../../common/Loader';
import { canVerifyPavAssets } from './pavConfig';
import PavAssetReviewCard, { PAV_ASSET_GRID_CLASS, getSavedAssetVerification } from './PavAssetReviewCard';
import PavSavedAssetEditForm from './PavSavedAssetEditForm';
import usePavAssetVerification from './usePavAssetVerification';
import {
    getMediaViewAttachments,
    getSavedAssetApiId,
    getSavedAssetIdentity,
    getSavedAssetLedgerId,
    getSavedAssetSerialSearchText,
    matchesSavedAssetSerial,
} from './pavAssetUtils';

const STATUS_FILTERS = [
    { key: 'ALL', label: 'All', icon: 'inventory_2' },
    { key: 'APPROVED', label: 'Approved', icon: 'check_circle' },
    { key: 'REJECTED', label: 'Rejected', icon: 'cancel' },
];

// Long enough for the spinner to register as feedback on a big list; the filter
// itself runs in memory, so nothing is actually being waited on.
const SEARCH_DEBOUNCE_MS = 250;

// Full view of the assets already saved on this milestone, opened from the
// header button so the verification screen stays focused on new assets. Each
// asset uses the shared review card, so it reads exactly like the authenticated
// verification modal and carries the same approve/reject decision.
export default function SavedPavAssets({ assets, assetTypes, orgId, isChat, isCompact, ticketId, milestoneId, onBack, onAssetUpdated, readOnly = false }) {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const [uploadingKey, setUploadingKey] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [savingKey, setSavingKey] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const { verifyAsset, getAssetOutcome, getPendingDecision } = usePavAssetVerification(ticketId);
    const canVerify = !readOnly && canVerifyPavAssets(newLoggedUserData, orgId);

    // `key` is the asset identity the update and verification callbacks match
    // on, and two rows sharing a serial and asset type produce the same one.
    // React needs a key that is unique per row: duplicates make it lose track of
    // the earlier fibers, which then survive a filter that should have removed
    // them - the reason a search for one serial still left other cards on screen.
    const assetOptions = useMemo(() => assets.map((asset, index) => {
        const key = getSavedAssetIdentity(asset, index);
        return {
            asset,
            key,
            rowKey: `${key}#${index}`,
            index,
            serialText: getSavedAssetSerialSearchText(asset),
        };
    }), [assets]);

    useEffect(() => {
        if (searchTerm === appliedSearchTerm) return undefined;
        const timer = setTimeout(() => setAppliedSearchTerm(searchTerm), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm, appliedSearchTerm]);
    const isSearching = searchTerm !== appliedSearchTerm;

    // Same precedence the review card uses: a decision taken in this session
    // wins over the one the asset was loaded with. `undefined` means undecided,
    // so it belongs to neither the approved nor the rejected filter.
    const getAssetVerification = useCallback((asset) => {
        const outcome = getAssetOutcome(asset);
        return outcome !== undefined ? outcome : getSavedAssetVerification(asset);
    }, [getAssetOutcome]);

    const statusCounts = useMemo(() => assetOptions.reduce((counts, option) => {
        const verification = getAssetVerification(option.asset);
        if (verification === true) counts.APPROVED += 1;
        else if (verification === false) counts.REJECTED += 1;
        return counts;
    }, { ALL: assetOptions.length, APPROVED: 0, REJECTED: 0 }), [assetOptions, getAssetVerification]);

    // Every saved asset is already in memory, so the search box and the decision
    // filter narrow the list here instead of asking the API for another page.
    const visibleOptions = useMemo(() => assetOptions.filter((option) => {
        if (!matchesSavedAssetSerial(option.serialText, appliedSearchTerm)) return false;
        if (statusFilter === 'ALL') return true;
        const verification = getAssetVerification(option.asset);
        return statusFilter === 'APPROVED' ? verification === true : verification === false;
    }), [assetOptions, appliedSearchTerm, statusFilter, getAssetVerification]);

    const trimmedSearchTerm = appliedSearchTerm.trim();
    const hasActiveFilter = Boolean(trimmedSearchTerm) || statusFilter !== 'ALL';
    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setStatusFilter('ALL');
    }, []);

    const previewAttachments = useCallback((attachments, openedIndex) => {
        const mediaAttachments = getMediaViewAttachments(attachments);
        if (mediaAttachments.length === 0) return;
        dispatch(openMediaViewModal({
            attachments: mediaAttachments,
            openedMediaIndex: Math.max(openedIndex, 0),
            showSenderDetail: false,
            isTicketChat: isChat,
        }));
    }, [dispatch, isChat]);

    const openFilePicker = (asset, assetKey) => {
        if (uploadingKey) return;
        uploadTargetRef.current = { asset, key: assetKey };
        fileInputRef.current?.click();
    };

    const handleAttachmentChange = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';
        const target = uploadTargetRef.current;
        uploadTargetRef.current = null;
        if (selectedFiles.length === 0 || uploadingKey || !target?.asset) return;

        const imageFiles = selectedFiles.filter((file) => file.type?.startsWith('image/'));
        if (imageFiles.length !== selectedFiles.length) {
            dispatch(sendToastMessage({ status: 'danger', message: 'Only image attachments are supported for PAV.' }));
        }
        if (imageFiles.length === 0) return;

        const assetId = getSavedAssetApiId(target.asset);
        const ledgerId = getSavedAssetLedgerId(target.asset);
        if (!assetId || !ledgerId) {
            dispatch(sendToastMessage({ status: 'danger', message: 'Unable to identify this asset for attachment update.' }));
            return;
        }

        setUploadingKey(target.key);
        dispatch(postImageUrl({
            files: imageFiles,
            ref: `pav-saved-${ticketId}-${milestoneId}-${ledgerId}-${Date.now()}`,
            purpose: 'PAV_ATTACHMENT',
            ticket_id: ticketId,
            milestone_id: milestoneId,
            onSuccess: (uploadedFiles) => {
                const newAttachments = (Array.isArray(uploadedFiles) ? uploadedFiles : [uploadedFiles])
                    .filter((attachment) => attachment?.file_url)
                    .map((attachment) => ({
                        name: attachment.name,
                        file_url: attachment.file_url,
                        mime_type: attachment.mime_type,
                        ocr_data: { raw_strings: ['different'] },
                    }));
                if (newAttachments.length === 0) {
                    setUploadingKey(null);
                    dispatch(sendToastMessage({ status: 'danger', message: 'Image upload completed without a file URL.' }));
                    return;
                }

                dispatch(updateAssets({
                    id: assetId,
                    formName: `pav_update_asset_attachments_${assetId}`,
                    payload: {
                        ledger_id: ledgerId,
                        attachments: newAttachments
                    },
                    closeModal: false,
                    refresh: false,
                    showToast: false,
                    onSuccess: (responseData) => {
                        onAssetUpdated?.({
                            assetKey: target.key,
                            responseData,
                            attachments: newAttachments,
                        });
                        dispatch(getServiceRequestById({ ticketId, callDetail: true }));
                        dispatch(sendToastMessage({ status: 'success', message: `${newAttachments.length} attachment${newAttachments.length === 1 ? '' : 's'} added successfully.` }));
                        setUploadingKey(null);
                    },
                    onError: (error) => {
                        dispatch(sendToastMessage({ status: 'danger', message: error?.response?.data?.messages?.[0] || 'Unable to update asset attachments.' }));
                        setUploadingKey(null);
                    },
                }));
            },
            onError: (error) => {
                dispatch(sendToastMessage({ status: 'danger', message: error?.response?.data?.messages?.[0] || error?.message || 'Image upload failed.' }));
                setUploadingKey(null);
            },
        }));
    };

    // Kept referentially stable so the memoized cards only re-render when an
    // upload actually starts or finishes.
    const renderAddImagesButton = useCallback((asset, assetKey) => {
        const isUploading = uploadingKey === assetKey;
        return (
            <button
                type='button'
                disabled={Boolean(uploadingKey)}
                onClick={() => openFilePicker(asset, assetKey)}
                className={`flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-scogolightblue font-semibold text-scogoprimary transition-colors ${isCompact ? 'h-6 px-1.5 text-font09' : 'h-8 px-2 text-font10'} ${uploadingKey ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:bg-blue-100'}`}
            >
                {isUploading ? <Loader size='12' thickness='2' color='#12355F' /> : <span className='material-icons text-font13'>add_photo_alternate</span>}
                {isUploading ? 'Uploading...' : 'Add images'}
            </button>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadingKey, isCompact]);

    const saveAssetFields = useCallback((asset, assetKey, { payload, assetPatch }) => {
        if (savingKey || uploadingKey) return;
        const assetId = getSavedAssetApiId(asset);
        const ledgerId = getSavedAssetLedgerId(asset);
        if (!assetId || !ledgerId) {
            dispatch(sendToastMessage({ status: 'danger', message: 'Unable to identify this asset for update.' }));
            return;
        }

        setSavingKey(assetKey);
        dispatch(updateAssets({
            id: assetId,
            formName: `pav_update_asset_fields_${assetId}`,
            payload: {
                ...payload,
                ledger_id: ledgerId,
            },
            closeModal: false,
            refresh: false,
            showToast: false,
            onSuccess: (responseData) => {
                onAssetUpdated?.({ assetKey, responseData, assetPatch });
                dispatch(getServiceRequestById({ ticketId, callDetail: true }));
                dispatch(sendToastMessage({ status: 'success', message: responseData?.message || 'Asset updated successfully.' }));
                setEditingKey(null);
                setSavingKey(null);
            },
            onError: (error) => {
                dispatch(sendToastMessage({ status: 'danger', message: error?.response?.data?.messages?.[0] || error?.message || 'Unable to update this asset.' }));
                setSavingKey(null);
            },
        }));
    }, [dispatch, onAssetUpdated, savingKey, ticketId, uploadingKey]);

    return (
        <div className={`flex min-h-0 flex-1 flex-col bg-slate-50 ${isChat ? 'h-[40vh]' : ''}`}>
            {!readOnly && <input ref={fileInputRef} type='file' accept='image/*' multiple className='hidden' onChange={handleAttachmentChange} />}
            {!isChat && (
                <div className='flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 py-2.5 shadow-sm'>
                    {typeof onBack === 'function' && (
                        <button type='button' onClick={onBack} aria-label='Back to asset verification' title='Back' className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-scogolightblue text-scogoprimary shadow-sm transition-all hover:border-scogoprimary hover:bg-blue-100'>
                            <span className='material-icons text-font16'>arrow_back</span>
                        </button>
                    )}
                    <p className='min-w-0 flex-1 text-font12 font-semibold text-scogo21'>Added assets</p>
                    <span className='inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-font10 font-semibold text-emerald-800'>
                        <span className='material-icons-outlined text-font13'>verified</span>
                        {assets.length} saved
                    </span>
                </div>
            )}

            {assetOptions.length > 0 && (
                <div className={`shrink-0 border-b border-slate-200 bg-white ${isCompact ? 'px-2 py-1.5' : isChat ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
                    <div className='flex flex-wrap items-center gap-2'>
                        <div className='relative min-w-[13rem] flex-1'>
                            <span className='material-icons-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-font16 text-slate-400'>search</span>
                            <input
                                type='text'
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder='Search by serial number'
                                aria-label='Search added assets by serial number'
                                className={`w-full rounded-lg border border-scogoddd bg-white pl-9 pr-9 font-normal text-scogo21 transition-colors placeholder:text-inputfieldtext focus:border-scogoprimary focus:outline-none focus:ring-0 ${isCompact ? 'h-10 text-font10' : 'h-12 text-font11'}`}
                            />
                            {isSearching ? (
                                <span className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2' aria-label='Searching' role='status'>
                                    <Loader size='14' thickness='2' color='#12355F' />
                                </span>
                            ) : searchTerm ? (
                                <button
                                    type='button'
                                    onClick={() => setSearchTerm('')}
                                    aria-label='Clear search'
                                    title='Clear search'
                                    className='absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-scogof8 hover:text-scogoprimary'
                                >
                                    <span className='material-icons text-font14'>close</span>
                                </button>
                            ) : null}
                        </div>

                        <div className={`flex shrink-0 items-center gap-0.5 rounded-lg border border-scogoddd bg-scogof8 p-0.5 ${isCompact ? 'h-10' : 'h-12'}`} role='group' aria-label='Filter by verification decision'>
                            {STATUS_FILTERS.map((filter) => {
                                const isActive = statusFilter === filter.key;
                                return (
                                    <button
                                        key={filter.key}
                                        type='button'
                                        onClick={() => setStatusFilter(filter.key)}
                                        aria-pressed={isActive}
                                        title={`Show ${filter.label.toLowerCase()} assets`}
                                        className={`flex h-full items-center gap-1 rounded-md px-2 font-medium transition-colors ${isCompact ? 'text-font09' : 'text-font10'} ${isActive ? 'bg-white text-scogoprimary shadow-sm' : 'text-slate-500 hover:text-scogoprimary'}`}
                                    >
                                        <span className={`material-icons-outlined ${isCompact ? 'text-font12' : 'text-font13'}`}>{filter.icon}</span>
                                        {!isCompact && <span>{filter.label}</span>}
                                        <span className={`rounded-full px-1 font-semibold ${isActive ? 'bg-scogolightblue text-scogoprimary' : 'bg-white text-slate-500'}`}>{statusCounts[filter.key]}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {hasActiveFilter && (
                        <p className='mt-1 text-font10 font-normal text-slate-500'>
                            {visibleOptions.length} of {assetOptions.length} asset{assetOptions.length === 1 ? '' : 's'} shown
                        </p>
                    )}
                </div>
            )}

            <div className={`min-h-0 flex-1 overflow-x-hidden overflow-y-auto ${isCompact ? 'p-1.5' : isChat ? 'p-2' : 'p-3'}`}>
                {assetOptions.length === 0 ? (
                    <p className='rounded-lg border border-dashed border-scogoddd bg-white px-4 py-6 text-center text-font11 font-normal text-slate-600'>No assets have been added to this milestone yet.</p>
                ) : visibleOptions.length === 0 ? (
                    <div className='rounded-lg border border-dashed border-scogoddd bg-white px-4 py-6 text-center'>
                        <p className='text-font11 font-normal text-slate-600'>
                            {trimmedSearchTerm ? `No added asset has a serial number matching "${trimmedSearchTerm}".` : 'No added asset matches this filter.'}
                        </p>
                        <button type='button' onClick={clearFilters} className='mt-1.5 text-font11 font-medium text-scogoprimary hover:text-scogoorange'>Clear search and filters</button>
                    </div>
                ) : (
                    <div className={isChat ? 'space-y-2' : PAV_ASSET_GRID_CLASS}>
                        {visibleOptions.map((option) => {
                            const isEditing = editingKey === option.key;
                            return (
                                <PavAssetReviewCard
                                    key={option.rowKey}
                                    asset={option.asset}
                                    assetKey={option.key}
                                    milestoneId={milestoneId}
                                    isCompact={isCompact}
                                    isNarrow={!isChat}
                                    canVerify={canVerify}
                                    canEdit={canVerify}
                                    isEditing={isEditing}
                                    editDisabled={Boolean(uploadingKey || savingKey || editingKey) && editingKey !== option.key}
                                    onEdit={() => setEditingKey(option.key)}
                                    editContent={isEditing ? (
                                        <PavSavedAssetEditForm
                                            asset={option.asset}
                                            assetTypes={assetTypes}
                                            orgId={orgId}
                                            isCompact={isCompact}
                                            isSaving={savingKey === option.key}
                                            isDisabled={Boolean(uploadingKey)}
                                            onCancel={() => setEditingKey(null)}
                                            onSubmit={(changes) => saveAssetFields(option.asset, option.key, changes)}
                                        />
                                    ) : null}
                                    outcome={getAssetOutcome(option.asset)}
                                    pendingDecision={getPendingDecision(option.key)}
                                    onVerify={verifyAsset}
                                    onPreview={previewAttachments}
                                    renderExtraActions={readOnly ? undefined : renderAddImagesButton}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
